import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import "./GlobalSearchBox.css";
import Button from "../Button/Button";
import { Menu, MenuItem } from "@mui/material";
import dropRoundLight from "../../Assets/Images/drop-down-round-light.svg";
import dropRoundDark from "../../Assets/Images/drop-down-round-dark.svg";
import StaticOptions from "../../Assets/Json/StaticOptions.json";
import Badge from "@mui/material/Badge";
import ChatPopup from "../../Common/VybogTal-Chat-Popup/ChatPopup";
import infoLightIcon from "../../Assets/Images/VybogTAL-Chat-info-icon.svg";
import infoDarkIcon from "../../Assets/Images/info-icon-Dark.svg";

const GlobalSearchBox = ({
  value,
  onChange,
  onSearch,
  onClear,
  showClearSearch = false,
  onFilterClick = () => "",
  isDarkMode,
  placeholder = "Search",
  suggestions = [],
  subText,
  searchTitle,
  titleIconLight,
  titleIconDark,
  titleIconAlt = "",
  buttonLabel,
  resetKey = 0,
  askmeanythingOnKeyDown = () => {},
  enableGlobalEnter = false,
  isSearchLoading=false,
  showBadge = false,
  showHowItWorks = false,
  resetSelectedSuggestion = false,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapse = () => setCollapsed(!collapsed);
  const [anchorEl, setAnchorEl] = useState(null);
  const [secondaryAnchorEl, setSecondaryAnchorEl] = useState(null);
  const [filterKey, setFilterKey] = useState(null);
  const [showSecondaryDropdown, setShowSecondaryDropdown] = useState(false);
  const [secondaryOptions, setSecondaryOptions] = useState([]);
  const [selectedSecondaryOption, setSelectedSecondaryOption] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(false);

  const searchTextAreaRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleChatPopupClose = useCallback(
    (selectedSuggestion) => {
      if (selectedSuggestion) {
        onChange({
          target: { value: selectedSuggestion, name: "globalsearchbox" },
        });
      }
      setIsChatPopupOpen(false);
    },
    [onChange],
  );

  const getLineCount = (text) => {
    if (!text) return 1;
    return text.split("\n").length;
  };

  useEffect(() => {
    if (suggestions.length > 0 && !selectedSuggestion) {
      setSelectedSuggestion(suggestions[0].name);
      setFilterKey(suggestions[0]);
      onFilterClick(suggestions[0]);
    }
  }, [suggestions, selectedSuggestion, onFilterClick]);

  useLayoutEffect(() => {
    const el = searchTextAreaRef.current;
    if (!el) return;

    const lineCount = getLineCount(value);
    const isCleared = !value || value.trim().length === 0;

    const previousHeight = el.style.height;
    try {
      el.style.height = "30px";
      const hasSoftWrap = el.scrollHeight > el.clientHeight + 1;

      let nextExpanded = isExpanded;
      if (isCleared && lineCount > 1) {
        nextExpanded = true;
      } else if (lineCount > 1 || hasSoftWrap) {
        nextExpanded = true;
      } else if (isCleared || (lineCount === 1 && !hasSoftWrap)) {
        nextExpanded = false;
      }

      if (nextExpanded !== isExpanded) {
        setIsExpanded(nextExpanded);
      }
    } finally {
      el.style.height = previousHeight;
    }
  }, [value]);

  useEffect(() => {
    if (!enableGlobalEnter) return;
    const handleGlobalEnter = (e) => {
      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        document.activeElement !== searchTextAreaRef.current
      ) {
        e.preventDefault();
        onSearch();
      }
    };
    document.addEventListener("keydown", handleGlobalEnter);
    return () => {
      document.removeEventListener("keydown", handleGlobalEnter);
    };
  }, [onSearch, enableGlobalEnter]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSecondaryClick = (event) =>
    setSecondaryAnchorEl(event.currentTarget);
  const handleSecondaryClose = () => setSecondaryAnchorEl(null);

  const handleSelect = (filterKey) => {
    setFilterKey(filterKey);
    setSelectedSuggestion(filterKey.name);
    onFilterClick(filterKey);
    handleClose();

    if (filterKey && filterKey.name === "Priority") {
      setShowSecondaryDropdown(true);
      setSecondaryOptions(StaticOptions.StaticOptions.clientPriority || []);
      setSelectedSecondaryOption(null);
    } else if (filterKey && filterKey.name === "Company Status") {
      setShowSecondaryDropdown(true);
      setSecondaryOptions(StaticOptions.StaticOptions.clientStatus || []);
      setSelectedSecondaryOption(null);
    }else if (filterKey && filterKey.name === "Vendor Status") {
      setShowSecondaryDropdown(true);
      setSecondaryOptions(StaticOptions.StaticOptions.vendorStatus || []);
      setSelectedSecondaryOption(null);
    } else {
      setShowSecondaryDropdown(false);
      setSecondaryOptions([]);
      setSelectedSecondaryOption(null);
    }
  };

  const handleSecondarySelect = (option) => {
    setSelectedSecondaryOption(option.title);
    handleSecondaryClose();
    onFilterClick({ ...filterKey, secondaryValue: option.title });
  };

  const handleClearSearch = () => {
    if (onClear) {
      onClear();
      if(resetSelectedSuggestion) setSelectedSuggestion(null);
    } else if (onChange) {
      onChange({ target: { name: "globalsearchbox", value: "" } });
    }
    setIsExpanded(false);
    searchTextAreaRef.current?.focus();
  };

  useEffect(() => {
    if (resetKey > 0) {
      setFilterKey(null);
      setSelectedSecondaryOption(null);
      setShowSecondaryDropdown(false);
      setSecondaryOptions([]);
      setSelectedSuggestion(null);
      setTimeout(() => {
        if (suggestions.length > 0) {
          setSelectedSuggestion(suggestions[0].name);
          setFilterKey(suggestions[0]);
          onFilterClick(suggestions[0]);
        }
      }, 0);
    }
  }, [resetKey]);

  const renderSuggestions = () => {
    return (
      <div className="search-by-dropdown">
        <span className="search-by-label">Search by</span>
        <div className="search-dropdown">
          <div
            className={`selected-option ${selectedSuggestion ? "active" : ""}`}
            onClick={handleClick}
          >
            {selectedSuggestion ||
              (suggestions.length > 0 ? suggestions[0].name : "Select")}
            <span className="dropdown-arrow">▼</span>
          </div>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            className="search-dropdown-menu"
            disableScrollLock={true}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              style: {
                backgroundColor: "var(--body__background)",
                color: "var(--header__color)",
                borderRadius: "10px",
                marginTop: "5px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                width: "auto",
                maxWidth: "100%",
                border: "1px solid #e6e5f4",
              },
            }}
            MenuListProps={{
              style: {
                padding: "8px 0",
                width: "100%",
              },
            }}
          >
            {suggestions.map((tag, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  handleSelect(tag);
                  setSelectedSuggestion(tag.name);
                }}
                selected={selectedSuggestion === tag.name}
                sx={{
                  padding: "10px 20px",
                  width: "100%",
                  whiteSpace: "normal",
                  fontFamily: "Satoshi",
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "20px",
                  "&:hover": {
                    backgroundColor: "var(--chat_tab_hover)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "var(--primary__color)",
                    color: "white",
                  },
                }}
              >
                {tag.name}
              </MenuItem>
            ))}
          </Menu>
        </div>
        {showSecondaryDropdown && (
          <div className="secondary-dropdown">
            <div
              className={`selected-option ${
                selectedSecondaryOption ? "active" : ""
              }`}
              onClick={handleSecondaryClick}
            >
              {selectedSecondaryOption || "Select"}
              <span className="dropdown-arrow">▼</span>
            </div>
            <Menu
              anchorEl={secondaryAnchorEl}
              open={Boolean(secondaryAnchorEl)}
              onClose={handleSecondaryClose}
              className="search-dropdown-menu"
              disableScrollLock={true}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                style: {
                  backgroundColor: "var(--body__background)",
                  color: "var(--header__color)",
                  borderRadius: "10px",
                  marginTop: "5px",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  width: "auto",
                  maxWidth: "100%",
                  border: "1px solid #e6e5f4",
                },
              }}
              MenuListProps={{
                style: {
                  padding: "8px 0",
                  width: "100%",
                },
              }}
            >
              {secondaryOptions.map((option, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleSecondarySelect(option)}
                  selected={selectedSecondaryOption === option.title}
                  sx={{
                    padding: "10px 20px",
                    width: "100%",
                    whiteSpace: "normal",
                    fontFamily: "Satoshi",
                    fontWeight: "400",
                    fontSize: "14px",
                    lineHeight: "20px",
                    "&:hover": {
                      backgroundColor: "var(--chat_tab_hover)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "var(--primary__color)",
                      color: "white",
                    },
                  }}
                >
                  {option.title}
                </MenuItem>
              ))}
            </Menu>
          </div>
        )}
      </div>
    );
  };

  // const howItWorksBadge = (
  //   <Badge
  //     badgeContent={
  //       <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
  //         <img
  //           src={isDarkMode ? infoDarkIcon : infoLightIcon}
  //           alt="info"
  //           style={{ width: "14px", height: "14px" }}
  //         />
  //         <span>How it Works</span>
  //       </div>
  //     }
  //     onClick={() => setIsChatPopupOpen(true)}
  //     sx={{
  //       cursor: "pointer",
  //       "& .MuiBadge-badge": {
  //         cursor: "pointer",
  //         fontSize: "10px",
  //         height: "24px",
  //         marginTop: "2px",
  //         minWidth: "32px",
  //         padding: "2px 8px",
  //         backgroundColor: isDarkMode
  //           ? "rgb(176 172 255 / 10%)"
  //           : "var(--skill-badge-bg)",
  //         color: isDarkMode ? "rgb(176 172 255)" : "#5c47ce",
  //         border: isDarkMode
  //           ? "1px solid rgb(176 172 255 / 40%)"
  //           : "1px solid #5c47ce",
  //         position: "static",
  //         transform: "none",
  //         borderRadius: "12px",
  //       },
  //     }}
  //   />
  // );

  return (
    <div className={`global-search-container${collapsed ? " collapsed" : ""}`}>
      <div
        className={`search-header${collapsed ? " collapsed" : ""}`}
        style={collapsed ? { borderRadius: "16px" } : { borderRadius: "" }}
      >
        {(titleIconLight || titleIconDark) && (
          <img
            src={
              isDarkMode
                ? titleIconDark || titleIconLight
                : titleIconLight || titleIconDark
            }
            alt={titleIconAlt}
            className="jelly-icon"
          />
        )}
        <span className="search-title">{searchTitle}</span>
        <span className="search-subtext">{subText}</span>
        <img
          src={isDarkMode ? dropRoundDark : dropRoundLight}
          style={{
            transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.25s ease",
          }}
          alt="Toggle"
          className={`dropdown-icon toggle-icon ${
            collapsed ? "collapsed" : ""
          }`}
          onClick={toggleCollapse}
        />
      </div>
      {!collapsed && (
        <>
          <div className="search-input-section-wrap">
            {suggestions?.length > 0 && renderSuggestions()}
            <div className="search-input-section">
              <div
                style={{
                  position: "relative",
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <textarea
                  ref={searchTextAreaRef}
                  value={value}
                  onChange={onChange}
                  name="globalsearchbox"
                  placeholder={placeholder}
                  disabled={isSearchLoading}
                  style={{
                    paddingLeft: "20px",
                    paddingRight: showHowItWorks ? "115px" : "10px",
                    width: "100%",
                    border: "1px solid var(--search-input-section-border)",
                    borderRight: "none",
                    borderRadius: "10px 0px 0px 10px",
                    backgroundColor: "var(--body__background)",
                    height: isExpanded ? "60px" : "30px",
                    color: "var(--header__color)",
                    paddingTop: "9px",
                    overflowY: isExpanded ? "auto" : "hidden",
                    opacity: isSearchLoading ? 0.6 : 1,
                    cursor: isSearchLoading ? "not-allowed" : "text",
                  }}
                  onKeyDown={askmeanythingOnKeyDown}
                />
                {/* {showHowItWorks && (
                  <div
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: isExpanded ? "12px" : "50%",
                      transform: isExpanded ? "none" : "translateY(-50%)",
                      zIndex: 10,
                    }}
                  >
                    {howItWorksBadge}
                  </div>
                )} */}
              </div>
              {showClearSearch && !isSearchLoading && (
                <div
                  className="simple-inline-actions"
                  style={{
                    border: "1px solid var(--search-input-section-border)",
                    borderLeft: "none",
                  }}
                >
                  <Button
                    label="✕ Clear Search"
                    event={handleClearSearch}
                    className="cephea-clear-search-button"
                  />
                </div>
              )}
              {/* {(filter === "true" &&
                <>
                <Button
                  label={filterKey ? filterKey.name : "Filters"}
                  iconSrc={filterIcon}
                  //event={onFilterClick}
                  className="filter-button-sharp-allside"
                  color="#777777"
                  style={{
                    borderTop: "1px solid var(--search-input-section-border)",
                    borderBottom: "1px solid var(--search-input-section-border)",
                    borderLeft: "none",
                    borderRight: "none",
                    backgroundColor: "var(--body__background)",
                    borderRadius: "0px",
                    padding: "10px 10px"
                  }}
                  backgroundColor="var(--body__background)"
                  iconWidth="20px"
                  hoverd={false}
                  event={handleClick}
                /> 
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                  {renderFilterMenuItems()}
                </Menu>    
                </>
                )} */}
              <Button
                label={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {buttonLabel}
                    {showBadge && (
                      <Badge
                        badgeContent="BETA"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "10px",
                            backgroundColor: "#e38608",
                            color: "#ffff",
                            position: "static",
                            transform: "none",
                            borderRadius: "12px",
                            fontWeight: "100",
                          },
                        }}
                      />
                    )}
                  </span>
                }
                // iconSrc={searchIcon}
                event={onSearch}
                disabled={isSearchLoading}
                backgroundColor="var(--search-button-background-color)"
                color="#ffffff"
                iconWidth="20px"
                className="search-button-sharp-left"
                hoverd={false}
                superShine={true}
                //   style={{ borderRadius: "0px 10px 10px 0px" }}
                style={{
                  borderTopLeftRadius: "0",
                  borderBottomLeftRadius: "0",
                  borderTopRightRadius: "10px",
                  borderBottomRightRadius: "10px",
                  backgroundColor: "var(--search-button-background-color)",
                  color: "#ffffff",
                  // padding: "10px 10px",
                  height: isExpanded ? "73px" : undefined,
                  opacity: isSearchLoading ? 0.6 : 1,
                  cursor: isSearchLoading ? "not-allowed" : "text",
                  padding: "12px 14px",
                }}
              />
            </div>
          </div>
        </>
      )}
      {isChatPopupOpen && (
        <ChatPopup onClose={handleChatPopupClose} isDarkMode={isDarkMode} />
      )}
    </div>
  );
};

export default GlobalSearchBox;